import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';

import { queryClient } from '@/lib/query-client';

export type UseCreateFinalCircuitOptions = {
    electricalProjectVersionId: string;
    onSuccess?: (finalCircuitId: string) => void;
};

export function useCreateFinalCircuitMutation({
    electricalProjectVersionId,
    onSuccess,
}: UseCreateFinalCircuitOptions) {
    return api.useMutation(
        'post',
        '/api/v2/electrical/projects/finalcircuits/create',
        {
            onMutate: async (variable) => {
                const finalCircuitListQueryOptions = elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({
                    electricalProjectVersionId,
                });
                const finalCircuitQueryOptions = elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                    electricalProjectFinalCircuitId: variable!.body!.ElectricalProjectFinalCircuitId!,
                });
                const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                    electricalProjectVersionId,
                });

                const previousDataList = queryClient.getQueryData(finalCircuitListQueryOptions.queryKey) as components['schemas']['ListElectricalProjectFinalCircuitQueryResult'];
                const previousDataSingle = queryClient.getQueryData(finalCircuitQueryOptions.queryKey) as components['schemas']['EditElectricalProjectFinalCircuitQueryResult'];

                await queryClient.cancelQueries(summaryOptions);
                await queryClient.cancelQueries(finalCircuitListQueryOptions);
                await queryClient.cancelQueries(finalCircuitQueryOptions);

                queryClient.setQueryData<components['schemas']['ListElectricalProjectFinalCircuitQueryResult']>(
                    finalCircuitListQueryOptions.queryKey,
                    (old) => {
                        if (old && variable) {
                            const updatedData: components['schemas']['ListElectricalProjectFinalCircuitQueryResult'] = {
                                ...old,
                                FinalCircuits: [
                                    ...(old.FinalCircuits || []),
                                    {
                                        ElectricalProjectFinalCircuitId: variable!.body!.ElectricalProjectFinalCircuitId!,
                                        Identifier: variable!.body!.Identifier,
                                        Kind: variable!.body!.Kind,
                                        FloorNumber: null,
                                        SpaceNumber: null,
                                        Order: 9999,
                                    },
                                ],
                            };
                            return updatedData;
                        }
                        return old;
                    },
                );

                queryClient.setQueryData<components['schemas']['EditElectricalProjectFinalCircuitQueryResult']>(
                    finalCircuitQueryOptions.queryKey,
                    (old) => {
                        const updatedData: components['schemas']['EditElectricalProjectFinalCircuitQueryResult'] = {
                            ...old,
                            Circuit: {
                                ElectricalProjectFinalCircuitId: variable!.body!.ElectricalProjectFinalCircuitId!,
                                Identifier: variable?.body?.Identifier,
                                Kind: variable?.body?.Kind,
                            },
                        };
                        return updatedData;
                    },
                );

                queryClient.setQueryData<components['schemas']['ViewElectricalProjectSummaryQueryResult']>(
                    summaryOptions.queryKey,
                    (old) => {
                        if (old && old.Summary) {
                            return {
                                ...old,
                                Summary: {
                                    ...old.Summary,
                                    ElectricalProjectVersionId: old.Summary.ElectricalProjectVersionId,
                                    FinalCircuitCount: (old.Summary.FinalCircuitCount || 0) + 1,
                                },
                            };
                        }
                        return old;
                    },
                );

                onSuccess?.(variable!.body!.ElectricalProjectFinalCircuitId!);

                return { previousDataList, previousDataSingle };
            },
            onSuccess: async (data) => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({ electricalProjectVersionId }),
                );

                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                        electricalProjectFinalCircuitId: data.ElectricalProjectFinalCircuitId,
                    }),
                );
            },
            onError: (error, _variable, context: any) => {
                console.error(error);

                queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({ electricalProjectVersionId }));

                if (context?.previousDataList) {
                    queryClient.setQueryData(
                        elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({ electricalProjectVersionId }).queryKey,
                        context.previousDataList,
                    );
                }

                if (context?.previousDataSingle) {
                    queryClient.setQueryData(
                        elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                            electricalProjectFinalCircuitId: context.previousDataSingle.Circuit.ElectricalProjectFinalCircuitId!,
                        }).queryKey,
                        context.previousDataSingle,
                    );
                }
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    );
}

export type UseCreateChecklistItemOptions = {
    electricalProjectVersionId: string;
    checklistItems: { ElectricalCheckListItemId: string; Description: string }[];
};

export function useCreateChecklistItemMutation({
    electricalProjectVersionId,
    checklistItems,
}: UseCreateChecklistItemOptions) {
    return api.useMutation('post', '/api/v2/electrical/projects/checklists/items/create', {
        onMutate: async (variable) => {
            const checkListItemsQueryOptions = elektraAppQueryOptions.project.inspection.finalCircuit.checkListItemsOptions({
                electricalProjectVersionId: variable!.body!.ElectricalProjectVersionId!,
                electricalProjectFinalCircuitId: variable!.body!.ObjectId!,
            });

            const previousDataList = queryClient.getQueryData(checkListItemsQueryOptions.queryKey) as components['schemas']['ListElectricalProjectCheckListItemQueryResult'];

            await queryClient.cancelQueries(checkListItemsQueryOptions);

            queryClient.setQueryData<components['schemas']['ListElectricalProjectCheckListItemQueryResult']>(
                checkListItemsQueryOptions.queryKey,
                (old) => {
                    const newItem = {
                        ConclusionKind: null,
                        Description: checklistItems.find(item => item.ElectricalCheckListItemId === variable!.body!.ElectricalCheckListItemId)?.Description,
                        ElectricalCheckListItemId: variable!.body!.ElectricalCheckListItemId,
                        ObjectKind: variable!.body!.ObjectKind,
                        ElectricalProjectFinalCircuitId: variable!.body!.ObjectId,
                        ElectricalProjectCheckListItemId: variable!.body!.ElectricalProjectCheckListItemId,
                    };

                    if (old) {
                        return {
                            Items: [...(old?.Items || []), newItem],
                            Count: (old?.Count || 0) + 1,
                        };
                    }

                    return {
                        Items: [newItem],
                        Count: 1,
                    };
                },
            );

            return { previousDataList };
        },
        onSettled: async (_data, _error, variable) => {
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.finalCircuit.checkListItemsOptions({
                    electricalProjectVersionId,
                    electricalProjectFinalCircuitId: variable!.body!.ObjectId!,
                }),
            );
        },
        scope: {
            id: electricalProjectVersionId,
        },
    });
}
